import { z } from 'zod'
import { timestampSchema, uuidSchema } from './common.ts'

export const ACTIVITY_ENTITIES = ['deal', 'client', 'order'] as const

export const activityEntitySchema = z.enum(ACTIVITY_ENTITIES)
export type ActivityEntity = z.infer<typeof activityEntitySchema>

export const ACTIVITY_KINDS = [
  'stage_changed',
  'manager_changed',
  'seller_changed',
  'note',
  'call',
  'email',
  'file_attached',
  'file_removed',
  'field_changed',
  'invoice_issued',
  'task_created',
  'task_updated',
  'task_completed',
  'task_cancelled',
  'production_stage_completed',
  'production_output_recorded',
] as const

export const activityKindSchema = z.enum(ACTIVITY_KINDS)
export type ActivityKind = z.infer<typeof activityKindSchema>

export const ACTIVITY_KIND_LABELS: Record<ActivityKind, string> = {
  stage_changed: 'Зміна статусу',
  manager_changed: 'Зміна менеджера',
  seller_changed: 'Зміна нашої компанії',
  note: 'Нотатка',
  call: 'Дзвінок',
  email: 'Лист',
  file_attached: 'Додано файл',
  file_removed: 'Видалено файл',
  field_changed: 'Зміна поля',
  invoice_issued: 'Виставлено рахунок',
  task_created: 'Створено завдання',
  task_updated: 'Оновлено завдання',
  task_completed: 'Завершено завдання',
  task_cancelled: 'Скасовано завдання',
  production_stage_completed: 'Завершено виробничий етап',
  production_output_recorded: 'Записано виготовлену кількість',
}

/**
 * Deliberately an open object rather than a union keyed on `kind`. Each kind's payload shape gets
 * pinned when the code that writes it lands; validating a shape nothing writes yet would only
 * encode a guess. The one thing worth enforcing today is that it stays an object, so a later
 * writer can add a field without a migration.
 */
export const activityPayloadSchema = z.record(z.string(), z.unknown())

export type ActivityPayload = z.infer<typeof activityPayloadSchema>

export const activitySchema = z.object({
  id: uuidSchema,
  entity: activityEntitySchema,
  entityId: uuidSchema,
  kind: activityKindSchema,
  /** Null for activities the system recorded itself, e.g. an overdue invoice flipping status. */
  actorId: z.string().min(1).nullable(),
  actorName: z.string().min(1).nullable().optional(),
  payload: activityPayloadSchema,
  occurredAt: timestampSchema,
})

export type Activity = z.infer<typeof activitySchema>

export const recordActivityInputSchema = z.object({
  entity: activityEntitySchema,
  entityId: uuidSchema,
  kind: activityKindSchema,
  payload: activityPayloadSchema.default({}),
})

export type RecordActivityInput = z.infer<typeof recordActivityInputSchema>

/** What a person can write on an order: a team note, or the record of a call or an email. */
export const DEAL_COMMENT_KINDS = ['note', 'call', 'email'] as const

export const createDealCommentInputSchema = z.object({
  text: z.string().trim().min(1, 'Напишіть коментар').max(5000),
  kind: z.enum(DEAL_COMMENT_KINDS).default('note'),
  /** One of this order's files, shown with the entry. */
  attachmentId: uuidSchema.nullable().optional(),
  /** Colleagues @mentioned in the text. Stored on the entry; nothing notifies them yet. */
  mentionIds: z.array(z.string().min(1)).max(20).default([]),
})

export type CreateDealCommentInput = z.infer<typeof createDealCommentInputSchema>
